import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Tag, SlidersHorizontal, Loader2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { apiFetch } from '@/lib/api';

// Rate card POR MOEDA + limites estruturais default. Substitui a edição de planos
// fixos — o modelo agora é pré-pago pay-as-you-go (preço por unidade + carteira).

interface Currency {
  code: string; active: boolean;
  rate_send_milli: number; rate_number_month_cents: number; rate_storage_gb_month_cents: number;
  rate_project_month_cents: number;
  message_free_count: number; number_free_count: number; storage_free_mb: number; project_free_count: number;
  retention_90d_cents: number; retention_365d_cents: number; retention_730d_cents: number;
  pro_month_cents: number; pct_number: number; pct_project: number; pct_storage_gb: number; pct_retention_30d: number;
}
interface Limits {
  max_projects: number; max_numbers_per_project: number; max_users: number; max_api_keys: number;
  storage_mb: number; media_retention_days: number; message_retention_days: number; rate_limit_rps: number;
  free_message_retention_days: number; free_media_retention_days: number;
}

const num = (v: string) => Math.max(0, Math.round(parseFloat(v.replace(',', '.')) || 0));

function MoneyField({ label, cents, onChange, suffix }: { label: string; cents: number; onChange: (cents: number) => void; suffix?: string }) {
  const [v, setV] = useState((cents / 100).toString());
  useEffect(() => { setV((cents / 100).toString()); }, [cents]);
  return (
    <label className="flex flex-col gap-1 text-sm">
      <span className="text-muted-foreground">{label}{suffix ? <span className="text-xs"> {suffix}</span> : null}</span>
      <input value={v} inputMode="decimal" onChange={(e) => { setV(e.target.value); onChange(Math.round((parseFloat(e.target.value.replace(',', '.')) || 0) * 100)); }}
        className="h-9 rounded-md border bg-background px-3" />
    </label>
  );
}

function IntField({ label, value, onChange }: { label: string; value: number; onChange: (v: number) => void }) {
  const [v, setV] = useState(value.toString());
  useEffect(() => { setV(value.toString()); }, [value]);
  return (
    <label className="flex flex-col gap-1 text-sm">
      <span className="text-muted-foreground">{label}</span>
      <input value={v} inputMode="numeric" onChange={(e) => { setV(e.target.value); onChange(num(e.target.value)); }}
        className="h-9 rounded-md border bg-background px-3" />
    </label>
  );
}

function CurrencyCard({ c }: { c: Currency }) {
  const [cur, setCur] = useState(c);
  const [saving, setSaving] = useState(false);
  useEffect(() => setCur(c), [c]);
  const save = async () => {
    setSaving(true);
    try {
      const r = await apiFetch('PUT', '/platform/currencies', cur);
      if (!r.ok) { toast.error(`Falha ao salvar ${cur.code}.`); return; }
      toast.success(`Rate card ${cur.code} salvo.`);
    } finally { setSaving(false); }
  };
  return (
    <Card>
      <CardHeader className="flex-row items-center justify-between">
        <CardTitle className="text-base">{cur.code}</CardTitle>
        <label className="flex items-center gap-2 text-xs text-muted-foreground"><input type="checkbox" checked={cur.active} onChange={(e) => setCur({ ...cur, active: e.target.checked })} /> ativa</label>
      </CardHeader>
      <CardContent className="grid gap-3 sm:grid-cols-2">
        {/* MODELO DE PLANO (atual): M = mensalidade do Pro; add-ons em % de M. */}
        <MoneyField label="⭐ Pro / mês (M)" suffix="indicador único" cents={cur.pro_month_cents} onChange={(v) => setCur({ ...cur, pro_month_cents: v })} />
        <IntField label="% número (de M)" value={cur.pct_number} onChange={(v) => setCur({ ...cur, pct_number: v })} />
        <IntField label="% projeto (de M)" value={cur.pct_project} onChange={(v) => setCur({ ...cur, pct_project: v })} />
        <IntField label="% +1GB (de M)" value={cur.pct_storage_gb} onChange={(v) => setCur({ ...cur, pct_storage_gb: v })} />
        <IntField label="% +30d retenção (de M)" value={cur.pct_retention_30d} onChange={(v) => setCur({ ...cur, pct_retention_30d: v })} />
        {/* Legado PAYG (mantido p/ compat enquanto o motor não migra): */}
        <MoneyField label="Envios (legado)" suffix="/ 1.000" cents={cur.rate_send_milli} onChange={(v) => setCur({ ...cur, rate_send_milli: v })} />
        <MoneyField label="Número" suffix="/ mês" cents={cur.rate_number_month_cents} onChange={(v) => setCur({ ...cur, rate_number_month_cents: v })} />
        <MoneyField label="Storage" suffix="/ GB·mês" cents={cur.rate_storage_gb_month_cents} onChange={(v) => setCur({ ...cur, rate_storage_gb_month_cents: v })} />
        <MoneyField label="Projeto" suffix="/ mês (excedente)" cents={cur.rate_project_month_cents} onChange={(v) => setCur({ ...cur, rate_project_month_cents: v })} />
        <IntField label="Mensagens grátis/mês" value={cur.message_free_count} onChange={(v) => setCur({ ...cur, message_free_count: v })} />
        <IntField label="Números grátis" value={cur.number_free_count} onChange={(v) => setCur({ ...cur, number_free_count: v })} />
        <IntField label="Storage grátis (MB)" value={cur.storage_free_mb} onChange={(v) => setCur({ ...cur, storage_free_mb: v })} />
        <IntField label="Projetos grátis" value={cur.project_free_count} onChange={(v) => setCur({ ...cur, project_free_count: v })} />
        <MoneyField label="Retenção 90 dias" suffix="/ mês" cents={cur.retention_90d_cents} onChange={(v) => setCur({ ...cur, retention_90d_cents: v })} />
        <MoneyField label="Retenção 1 ano" suffix="/ mês" cents={cur.retention_365d_cents} onChange={(v) => setCur({ ...cur, retention_365d_cents: v })} />
        <MoneyField label="Retenção 2 anos" suffix="/ mês" cents={cur.retention_730d_cents} onChange={(v) => setCur({ ...cur, retention_730d_cents: v })} />
        <div className="flex items-end"><Button size="sm" disabled={saving} onClick={save}>{saving ? <Loader2 className="size-4 animate-spin" /> : null} Salvar {cur.code}</Button></div>
      </CardContent>
    </Card>
  );
}

function DefaultsCard({ init }: { init: Limits }) {
  const [l, setL] = useState(init);
  const [saving, setSaving] = useState(false);
  useEffect(() => setL(init), [init]);
  const save = async () => {
    setSaving(true);
    try {
      const r = await apiFetch('PUT', '/platform/defaults', l);
      if (!r.ok) { toast.error('Falha ao salvar os defaults.'); return; }
      toast.success('Limites default salvos.');
    } finally { setSaving(false); }
  };
  return (
    <Card>
      <CardHeader className="flex-row items-center gap-2"><SlidersHorizontal className="size-5 text-primary" /><CardTitle>Limites default (guard-rails grátis)</CardTitle></CardHeader>
      <CardContent className="grid gap-3 sm:grid-cols-3 lg:grid-cols-4">
        <IntField label="Projetos" value={l.max_projects} onChange={(v) => setL({ ...l, max_projects: v })} />
        <IntField label="Números/projeto" value={l.max_numbers_per_project} onChange={(v) => setL({ ...l, max_numbers_per_project: v })} />
        <IntField label="Usuários" value={l.max_users} onChange={(v) => setL({ ...l, max_users: v })} />
        <IntField label="API keys" value={l.max_api_keys} onChange={(v) => setL({ ...l, max_api_keys: v })} />
        <IntField label="Storage (MB)" value={l.storage_mb} onChange={(v) => setL({ ...l, storage_mb: v })} />
        <IntField label="Retenção mídia (dias)" value={l.media_retention_days} onChange={(v) => setL({ ...l, media_retention_days: v })} />
        <IntField label="Retenção msg (dias)" value={l.message_retention_days} onChange={(v) => setL({ ...l, message_retention_days: v })} />
        <IntField label="Rate limit (rps)" value={l.rate_limit_rps} onChange={(v) => setL({ ...l, rate_limit_rps: v })} />
        {/* Janela do FREE. As de cima valem para o Pro e para quem tem override. */}
        <IntField label="Free: retenção msg (dias)" value={l.free_message_retention_days} onChange={(v) => setL({ ...l, free_message_retention_days: v })} />
        <IntField label="Free: retenção mídia (dias)" value={l.free_media_retention_days} onChange={(v) => setL({ ...l, free_media_retention_days: v })} />
        <p className="text-xs text-muted-foreground sm:col-span-3 lg:col-span-4">
          A janela do Free vale para conta Free <strong>sem override próprio</strong> — e toda conta
          anterior a 10/10/2026 tem override (grandfathering da migração 0081), então reduzir aqui
          não mexe nelas. <strong>Encurtar apaga histórico e a mídia junto</strong> na varredura
          seguinte, e isso não se desfaz.
        </p>
        <div className="flex items-end sm:col-span-3 lg:col-span-4"><Button size="sm" disabled={saving} onClick={save}>{saving ? <Loader2 className="size-4 animate-spin" /> : null} Salvar defaults</Button></div>
      </CardContent>
    </Card>
  );
}

export function AdminPlans() {
  const currencies = useQuery({ queryKey: ['platform-currencies'], queryFn: async (): Promise<Currency[]> => ((await apiFetch('GET', '/platform/currencies')).data as { data?: Currency[] })?.data ?? [] });
  const defaults = useQuery({ queryKey: ['platform-defaults'], queryFn: async (): Promise<Limits> => (await apiFetch('GET', '/platform/defaults')).data as Limits });

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center gap-2"><Tag className="size-5 text-primary" /><h2 className="text-lg font-semibold">Rate card (pré-pago)</h2></div>
      <p className="text-sm text-muted-foreground">Preço por unidade em cada moeda (sem câmbio). Tudo em centavos no banco; aqui você edita na unidade maior.</p>
      <div className="grid gap-4 lg:grid-cols-3">
        {(currencies.data ?? []).map((c) => <CurrencyCard key={c.code} c={c} />)}
      </div>
      {defaults.data && <DefaultsCard init={defaults.data} />}
    </div>
  );
}
